(function installStorefrontReplay(global) {
  'use strict';
  const ENDPOINT = 'https://replay.downunderthunder.com.au';
  const STORAGE_KEY = 'dut-replay-customer-session-v1';
  const VISIT_KEY = 'dut-replay-customer-visit-v2';
  const OWNER_KEY = 'dut-replay-customer-owner-v2';
  const STREAM_KEY = 'dut-replay-customer-stream-v2';
  const ENTRY_KEY = 'dut-replay-entry-context-v1';
  const OUTBOX_KEY = 'dut-replay-pending-batches-v1';
  const VISIT_IDLE_MS = 30 * 60_000;
  const ORIGINS = new Set(['https://downunderthunder.com.au', 'https://www.downunderthunder.com.au', 'https://downunderthunderr.myshopify.com', 'http://127.0.0.1:9292', 'http://localhost:9292']);

  function allowedPage(window) {
    if (!ORIGINS.has(window.location.origin) || window.Shopify?.designMode === true) return false;
    let path;
    try { path = decodeURIComponent(window.location.pathname); } catch { return false; }
    return /^(?:\/$|\/(?:collections|products|blogs|pages|policies)(?:\/|$)|\/cart\/?$)/.test(path)
      && !/(?:^|\/)(?:accounts?|checkouts?|orders?|contact(?:-us)?|search|quiz|couples-quiz|solo-quiz|login|register|reset|recover)(?:\/|$|[.-])/i.test(path)
      && !/[?\u0000-\u001f@#]/.test(path) && path.length <= 600;
  }

  function processingAllowed(window) {
    const api = window.Shopify?.customerPrivacy;
    try {
      return typeof api?.analyticsProcessingAllowed === 'function' && api.analyticsProcessingAllowed() === true;
    } catch { return false; }
  }

  function automatedClient(window) {
    return window.navigator?.webdriver === true || /(?:Googlebot|AdsBot|Bingbot|BingPreview|DuckDuckBot|YandexBot|Baiduspider|Applebot|PetalBot|AhrefsBot|SemrushBot|Screaming Frog|facebookexternalhit|Twitterbot|HeadlessChrome|PhantomJS|\b(?:crawler|spider|selenium|playwright|puppeteer)\b)/i.test(window.navigator?.userAgent || '');
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { allowedPage, processingAllowed, automatedClient };
  if (!global?.document || global.__dutReplayLoader) return;
  if (automatedClient(global)) return;
  if (!allowedPage(global)) {
    // Private pages load no capture assets and send no requests. Keep only a
    // generic local gap so a later public page can explain the missing interval.
    if (ORIGINS.has(global.location.origin) && global.Shopify?.designMode !== true) {
      try {
        const visit = JSON.parse(global.localStorage.getItem(VISIT_KEY) || 'null');
        if (visit && !visit.gapStartedAt) global.localStorage.setItem(VISIT_KEY, JSON.stringify({ ...visit, gapStartedAt: Date.now(), gapReason: 'private_page' }));
      } catch {}
    }
    return;
  }
  global.__dutReplayLoader = true;

  let epoch = 0, configGeneration = null, enabled = false;
  let pending = null, refreshing = false, privacyRequested = false, clientStarting = false, departing = false;
  let lastAttempt = 0, scriptsReady = null;
  let initialCheckStarted = false, bootstrapAborter = null, preparedSession = null, documentStreamId = null, departureSent = false;
  let departureHandle = null, resumableDeparture = false;
  let boundaryReason = null, boundaryAt = 0;
  const assetCancels = new Set();
  const document = global.document;
  let wakeInstalled = false, humanInteracted = false;

  function wake(event) {
    if (event.isTrusted !== true || document.hidden || !allowedPage(global)) return;
    // Remember only that real input happened, never its contents. A quick
    // shopper may interact before the deferred eligibility response arrives.
    const firstInput = !humanInteracted;
    humanInteracted = true;
    if (firstInput) entryContext(true);
    if (!enabled) { if (configGeneration === null) initialRefresh(); return; }
    privacyAPI();
    if (!processingAllowed(global)) return;
    const status = global.DUTReplay?.status();
    if (status?.recording || status?.preparing) return;
    lastAttempt = 0; start(epoch);
  }

  function wakeListeners(on) {
    if (on === wakeInstalled) return;
    wakeInstalled = on;
    for (const name of ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel']) {
      if (on) document.addEventListener(name, wake, { passive: true, capture: true });
      else document.removeEventListener(name, wake, { capture: true });
    }
    for (const name of ['click', 'submit']) {
      if (on) document.addEventListener(name, privateBoundary, { passive: true, capture: true });
      else document.removeEventListener(name, privateBoundary, { capture: true });
    }
  }

  function withdrawRecorder(preserveVisit = false, reason = 'configuration_changed') {
    // A deliberately started local/owner test has separate storage and consent.
    // The customer switch must never interrupt that fictional test capture.
    if (global.DUTReplay?.status().testOnly === false) global.DUTReplay.withdraw(preserveVisit ? { preserveVisit: true, resumable: true, reason } : undefined);
  }

  function clearSession(shared = true) {
    try { global.sessionStorage.removeItem(STORAGE_KEY); } catch {}
    if (shared) {
      departureHandle = null; resumableDeparture = false;
      try { global.localStorage.removeItem(VISIT_KEY); global.localStorage.removeItem(OWNER_KEY); global.sessionStorage.removeItem(STREAM_KEY); global.sessionStorage.removeItem(ENTRY_KEY); global.sessionStorage.removeItem(OUTBOX_KEY); } catch {}
    }
  }

  function withdraw(preserveVisit = false) {
    epoch++;
    wakeListeners(false);
    pending?.abort(); pending = null;
    bootstrapAborter?.abort(); bootstrapAborter = null; preparedSession = null;
    departureHandle = null; resumableDeparture = false;
    for (const cancel of [...assetCancels]) cancel();
    withdrawRecorder(preserveVisit);
    clearSession(!preserveVisit);
  }

  function currentSharedVisit(captureGeneration) {
    try {
      const visit = JSON.parse(global.localStorage.getItem(VISIT_KEY) || 'null');
      return visit?.endpoint === ENDPOINT && visit.captureGeneration === captureGeneration && typeof visit.visitKey === 'string';
    } catch { return false; }
  }

  function activeSharedVisit(captureGeneration) {
    try {
      const visit = JSON.parse(global.localStorage.getItem(VISIT_KEY) || 'null');
      const now = Date.now();
      return visit?.endpoint === ENDPOINT && visit.captureGeneration === captureGeneration
        && /^[a-f\d]{8}(?:-[a-f\d]{4}){3}-[a-f\d]{12}$/i.test(visit.visitKey || '')
        && Number.isFinite(visit.lastActivityAt) && visit.lastActivityAt <= now
        && now - visit.lastActivityAt < VISIT_IDLE_MS;
    } catch { return false; }
  }

  function publicPath() { return allowedPage(global) ? global.location.pathname : null; }

  function entryContext(fromInput = false, reset = false) {
    if (!processingAllowed(global)) return null;
    const now = Date.now(), path = publicPath();
    if (!path) return null;
    let saved = null;
    try { saved = JSON.parse(global.sessionStorage.getItem(ENTRY_KEY) || 'null'); } catch {}
    const valid = !reset && saved?.endpoint === ENDPOINT && Number.isSafeInteger(saved.entryAt) && saved.entryAt > now - VISIT_IDLE_MS && saved.entryAt <= now
      && Array.isArray(saved.pages) && saved.pages.length > 0 && saved.pages.length <= 32 && saved.pages[0].path === saved.entryPath && saved.pages[0].timestamp === saved.entryAt
      && saved.pages.every((page, index) => typeof page.path === 'string' && allowedPage({ Shopify: global.Shopify, location: { origin: global.location.origin, pathname: page.path } })
        && Number.isSafeInteger(page.timestamp) && page.timestamp >= saved.entryAt && page.timestamp <= now && (!index || page.timestamp >= saved.pages[index - 1].timestamp));
    if (!valid) saved = fromInput ? { endpoint: ENDPOINT, entryPath: path, entryAt: now, pages: [{ path, timestamp: now }] } : null;
    if (!saved) return null;
    if (saved.pages.at(-1).path !== path && saved.pages.length < 32) saved.pages.push({ path, timestamp: now });
    try { global.sessionStorage.setItem(ENTRY_KEY, JSON.stringify(saved)); } catch {}
    return { entryPath: saved.entryPath, entryAt: saved.entryAt, pages: saved.pages };
  }

  function uuid() {
    if (typeof global.crypto?.randomUUID === 'function') return global.crypto.randomUUID();
    const bytes = new Uint8Array(16); global.crypto.getRandomValues(bytes);
    bytes[6] = bytes[6] & 15 | 64; bytes[8] = bytes[8] & 63 | 128;
    const hex = [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }

  async function prepareSession(thisEpoch) {
    if (preparedSession) return preparedSession;
    let visit;
    const context = entryContext(humanInteracted);
    const claim = (rejectedKey = null) => {
      if (!eligible(thisEpoch)) throw Error('Replay permission changed.');
      try { visit = JSON.parse(global.localStorage.getItem(VISIT_KEY) || 'null'); } catch { visit = null; }
      if (!visit || visit.endpoint !== ENDPOINT || visit.captureGeneration !== configGeneration || rejectedKey && visit.visitKey === rejectedKey) {
        visit = { visitKey: uuid(), endpoint: ENDPOINT, captureGeneration: configGeneration, lastActivityAt: Date.now(), ...(context ? { entryContext: context } : {}) };
        if (rejectedKey) { const fresh = entryContext(true, true); if (fresh) visit.entryContext = fresh; }
        global.localStorage.setItem(VISIT_KEY, JSON.stringify(visit));
      }
      if (!documentStreamId) documentStreamId = uuid();
      global.sessionStorage.setItem(STREAM_KEY, documentStreamId);
      global.localStorage.setItem(OWNER_KEY, JSON.stringify({ visitKey: visit.visitKey, streamId: documentStreamId, captureGeneration: configGeneration }));
      if (context && (!visit.entryContext || context.entryAt === visit.entryContext.entryAt)) {
        visit.entryContext = context; global.localStorage.setItem(VISIT_KEY, JSON.stringify(visit));
      }
    };
    const locked = callback => global.navigator?.locks?.request ? global.navigator.locks.request('dut-replay-visit-v2', callback) : callback();
    await locked(() => claim());
    const controller = new global.AbortController(); bootstrapAborter = controller;
    const timeout = global.setTimeout(() => controller.abort(), 5000);
    const create = () => global.fetch(ENDPOINT + '/api/sessions', { method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', priority: 'low', signal: controller.signal,
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: publicPath(), viewport: { width: global.innerWidth, height: global.innerHeight }, device: global.innerWidth < 750 ? 'mobile' : 'desktop',
        testOnly: false, visitKey: visit.visitKey, streamId: documentStreamId,
        ...(visit.entryContext?.entryAt > Date.now() - VISIT_IDLE_MS ? { entryContext: visit.entryContext } : {}) }) });
    try {
      let response = await create();
      if (response.status === 410 && eligible(thisEpoch)) {
        const failure = await response.json();
        if (['visit_inactive', 'visit_ended'].includes(failure.error)) { const rejected = visit.visitKey; await locked(() => claim(rejected)); response = await create(); }
      }
      if (!response.ok) throw Error('Replay handshake unavailable.');
      const result = await response.json();
      if (!eligible(thisEpoch) || result.captureGeneration !== configGeneration || !/^[a-f\d-]{36}$/i.test(result.id || '') || typeof result.writeToken !== 'string' || result.writeToken.length < 20) throw Error('Replay handshake unavailable.');
      preparedSession = { ...result, visitKey: visit.visitKey, streamId: documentStreamId };
      // Keep only this document's issued append handle for one final tiny close
      // signal. Pausing capture may invalidate its bootstrap/token cache.
      departureHandle = { ...preparedSession, endpoint: ENDPOINT }; resumableDeparture = false;
      global.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...preparedSession, endpoint: ENDPOINT, testOnly: false, ownerControlled: true }));
      return preparedSession;
    } finally { global.clearTimeout(timeout); if (bootstrapAborter === controller) bootstrapAborter = null; }
  }

  function departure(reason = 'document_departure') {
    if (reason === 'document_departure' && departureSent || !processingAllowed(global) || !publicPath()) return;
    let visit = null;
    try { visit = JSON.parse(global.localStorage.getItem(VISIT_KEY) || 'null'); } catch {}
    const saved = departureHandle;
    const sameHandle = saved?.endpoint === ENDPOINT && saved.streamId === documentStreamId && saved.visitKey === visit?.visitKey
      && saved.captureGeneration === configGeneration && visit?.captureGeneration === configGeneration && visit?.endpoint === ENDPOINT;
    if (!enabled && !(reason === 'document_departure' && resumableDeparture && sameHandle)) return;
    const timestamp = Date.now();
    if (reason !== 'document_departure' && boundaryReason === reason && timestamp - boundaryAt < 1000) return;
    if (global.DUTReplay?.depart?.(reason)) {
      if (reason === 'document_departure') departureSent = true;
      else { boundaryReason = reason; boundaryAt = timestamp; }
      return;
    }
    if (!sameHandle) return;
    if (reason === 'document_departure') departureSent = true;
    else { boundaryReason = reason; boundaryAt = timestamp; }
    global.fetch(ENDPOINT + '/api/sessions/' + encodeURIComponent(saved.id) + '/events', { method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', priority: 'low', keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + saved.writeToken }, body: JSON.stringify({ sequence: saved.nextSequence || 0, batchId: uuid(), streamId: documentStreamId,
        lastActivityAt: saved.lastActivityAt, path: publicPath(), events: [], markers: [{ type: 'end', reason, timestamp, path: publicPath() }] }) }).catch(() => {});
  }

  function privateBoundary(event) {
    if (event.isTrusted !== true || !enabled || !processingAllowed(global) || !publicPath()) return;
    const target = event.target?.closest?.('a[href],button,input');
    let reason = null;
    try {
      const base = global.location.origin + global.location.pathname;
      const form = event.type === 'submit' ? event.target : target?.closest?.('form');
      const button = event.type === 'submit' ? event.submitter : target;
      let destination;
      if (target?.tagName === 'A') {
        if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || target.getAttribute('target') === '_blank' || target.getAttribute('download') !== null) return;
        destination = new URL(target.getAttribute('href'), base);
      } else if (form) {
        destination = new URL(button?.getAttribute?.('formaction') || form.getAttribute('action') || '', base);
        if (destination.pathname === '/cart' && button?.getAttribute?.('name') === 'checkout') reason = 'checkout_departure';
      }
      if (destination?.origin === global.location.origin) {
        const path = decodeURIComponent(destination.pathname);
        if (/^\/checkouts?(?:\/|$)/i.test(path)) reason = 'checkout_departure';
        else if (!reason && /(?:^|\/)(?:accounts?|orders?|contact(?:-us)?|search|quiz|couples-quiz|solo-quiz|login|register|reset|recover)(?:\/|$|[.-])/i.test(path)) reason = 'private_page';
      }
    } catch {}
    if (reason) departure(reason);
  }

  function eligible(thisEpoch) {
    return thisEpoch === epoch && enabled && !document.hidden && allowedPage(global) && processingAllowed(global);
  }

  function privacyAPI() {
    if (global.Shopify?.customerPrivacy || privacyRequested || typeof global.Shopify?.loadFeatures !== 'function') return;
    privacyRequested = true;
    try {
      global.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        privacyRequested = false;
        if (!error && enabled && processingAllowed(global)) refresh();
      });
    } catch { privacyRequested = false; }
  }

  function script(path, present, thisEpoch) {
    if (present()) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const node = document.createElement('script');
      node.src = ENDPOINT + path; node.async = true;
      node.crossOrigin = 'anonymous'; node.referrerPolicy = 'no-referrer'; node.fetchPriority = 'low';
      const timeout = global.setTimeout(() => finish(Error('Replay asset timed out.')), 10_000);
      const cancel = () => finish(Error('Replay permission changed.'));
      assetCancels.add(cancel);
      const finish = error => {
        global.clearTimeout(timeout); node.onload = node.onerror = null;
        assetCancels.delete(cancel);
        if (error || !eligible(thisEpoch)) { node.remove(); reject(error || Error('Replay permission changed.')); }
        else resolve();
      };
      node.onload = () => finish(present() ? null : Error('Replay asset is unavailable.'));
      node.onerror = () => finish(Error('Replay asset is unavailable.'));
      document.head.appendChild(node);
    });
  }

  async function start(thisEpoch) {
    if (!eligible(thisEpoch) || clientStarting || global.DUTReplay?.status().sessionId || global.DUTReplay?.status().recording || global.DUTReplay?.status().preparing) return;
    // An idle page or ordinary crawler never loads the recorder or creates a
    // visit. A previously active human visit continues through navigation.
    if (!humanInteracted && !activeSharedVisit(configGeneration) && !entryContext()) return;
    // Failures remain invisible to the shopper and never interrupt shopping.
    if (Date.now() - lastAttempt < 15_000) return;
    lastAttempt = Date.now();
    clientStarting = true;
    try {
      const bootstrap = await prepareSession(thisEpoch);
      if (!scriptsReady) {
        scriptsReady = script('/vendor/rrweb-record.js', () => typeof global.DUTRecorderVendor?.record === 'function', thisEpoch)
          .then(() => { if (!eligible(thisEpoch)) throw Error('Replay permission changed.'); return script('/recorder.js', () => typeof global.DUTReplay?.start === 'function', thisEpoch); });
        scriptsReady.catch(() => { scriptsReady = null; });
      }
      await scriptsReady;
      if (!eligible(thisEpoch)) return;
      await global.DUTReplay.start({ endpoint: ENDPOINT, testOnly: false, bootstrap });
      if (thisEpoch === epoch && !eligible(thisEpoch)) {
        if (document.hidden || departing) global.DUTReplay?.suspend?.('hidden'); else withdrawRecorder();
      }
    } catch {
      if (thisEpoch === epoch) {
        // A temporary asset/connection failure ends work, not the visit's
        // recoverable identity. Retry only on the next public/visible visit.
        const status = global.DUTReplay?.status();
        if (status?.resumable && ['navigation', 'hidden', 'idle'].includes(status.reason)) {
          if (enabled && !departing) wakeListeners(true);
        } else { enabled = false; wakeListeners(false); }
        if (status?.resumable !== true) withdrawRecorder(true, 'technical_failure');
        clearSession(false);
        resumableDeparture = Boolean(departureHandle);
      }
    } finally { clientStarting = false; }
  }

  async function refresh() {
    if (refreshing || document.hidden) return;
    if (!allowedPage(global)) {
      enabled = false; wakeListeners(false);
      global.DUTReplay?.suspend?.('private_page');
      return;
    }
    refreshing = true;
    const thisEpoch = epoch, controller = new global.AbortController();
    pending = controller;
    const timeout = global.setTimeout(() => controller.abort(), 5_000);
    try {
      const response = await global.fetch(ENDPOINT + '/capture/config', { mode: 'cors', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', signal: controller.signal, priority: 'low' });
      if (!response.ok) throw Error('Replay config unavailable.');
      const config = await response.json();
      if (thisEpoch !== epoch || document.hidden) return;
      if (!Number.isSafeInteger(config.generation) || config.generation < 0) throw Error('Replay config unavailable.');
      const changed = configGeneration !== null && configGeneration !== config.generation;
      configGeneration = config.generation;
      enabled = config.enabled === true && config.eligible === true;
      if (!enabled) humanInteracted = false;
      if (!enabled) withdraw();
      else if (global.Shopify?.customerPrivacy && !processingAllowed(global)) { humanInteracted = false; withdraw(); }
      else if (changed) withdraw(currentSharedVisit(config.generation));
      if (changed) lastAttempt = 0;
      if (enabled) {
        if (!global.Shopify?.customerPrivacy || processingAllowed(global)) wakeListeners(true);
        if (humanInteracted || activeSharedVisit(configGeneration) || entryContext()) {
          privacyAPI();
          if (processingAllowed(global)) await start(epoch);
        }
      }
    } catch {
      if (thisEpoch === epoch) {
        enabled = false; epoch++; wakeListeners(false);
        for (const cancel of [...assetCancels]) cancel();
        global.DUTReplay?.suspend?.('technical_failure');
        clearSession(false);
        resumableDeparture = Boolean(departureHandle);
      }
    } finally {
      global.clearTimeout(timeout);
      if (pending === controller) pending = null;
      refreshing = false;
    }
  }

  document.addEventListener('visitorConsentCollected', () => {
    if (!processingAllowed(global)) { humanInteracted = false; withdraw(); }
    else if (enabled) { lastAttempt = 0; refresh(); }
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) { lastAttempt = 0; refresh(); }
  });
  global.addEventListener('dut-replay-status', event => {
    if (event.detail?.testOnly === false && event.detail.sessionId === null && event.detail.resumable === true) {
      // A later foreground/idle wake must renew the lightweight handshake,
      // rather than replaying the original document's now-expired response.
      preparedSession = null;
      resumableDeparture = event.detail.reason === 'technical_failure';
      // Hidden/idle/other-tab pauses leave no heavy work. Existing owner ON
      // permission permits a cheap trusted interaction to claim the foreground.
      if (['technical_failure', 'storage_limit'].includes(event.detail.reason)) { enabled = false; wakeListeners(false); }
      else if (enabled && !departing && processingAllowed(global)) wakeListeners(true);
      return;
    }
    if (event.detail?.testOnly === false && event.detail.sessionId === null && event.detail.navigationPaused !== true) {
      // OFF/control failure cancels everything without recursively stopping the
      // recorder again. A new page or visible visit may recheck the owner switch.
      enabled = false; epoch++; wakeListeners(false);
      humanInteracted = false;
      pending?.abort(); pending = null;
      bootstrapAborter?.abort(); bootstrapAborter = null;
      for (const cancel of [...assetCancels]) cancel();
      clearSession();
      preparedSession = null;
    }
  });
  global.addEventListener('popstate', () => {
    if (!allowedPage(global)) { enabled = false; wakeListeners(false); global.DUTReplay?.suspend?.('private_page'); }
    else { lastAttempt = 0; refresh(); }
  });
  global.addEventListener('pagehide', () => {
    departure();
    departing = true;
    wakeListeners(false);
    epoch++; pending?.abort(); pending = null;
    bootstrapAborter?.abort(); bootstrapAborter = null;
    for (const cancel of [...assetCancels]) cancel();
  });
  global.addEventListener('pageshow', event => {
    departing = false; departureSent = false;
    if (!event.persisted) return;
    preparedSession = null; lastAttempt = 0;
    const thisEpoch = epoch;
    const drain = enabled && processingAllowed(global) && allowedPage(global) ? global.DUTReplay?.prepareRestore?.() : null;
    Promise.resolve(drain).then(() => {
      if (thisEpoch === epoch && !departing) return refresh();
    }).catch(() => {});
  });
  global.addEventListener('storage', event => {
    if (event.key !== VISIT_KEY || event.newValue !== null) return;
    try { if (global.localStorage.getItem(VISIT_KEY) !== null) return; } catch {}
    enabled = false; epoch++; wakeListeners(false);
    humanInteracted = false;
    pending?.abort(); pending = null;
    bootstrapAborter?.abort(); bootstrapAborter = null; preparedSession = null;
    departureHandle = null; resumableDeparture = false;
    for (const cancel of [...assetCancels]) cancel();
    withdrawRecorder(); clearSession(false);
    try { global.sessionStorage.removeItem(ENTRY_KEY); global.sessionStorage.removeItem(OUTBOX_KEY); global.sessionStorage.removeItem(STREAM_KEY); } catch {}
  });
  function deferredStart() {
    if (initialCheckStarted) return;
    if (typeof global.requestIdleCallback === 'function') global.requestIdleCallback(initialRefresh, { timeout: 3000 });
    else global.setTimeout(initialRefresh, 1500);
  }
  function initialRefresh() { if (!initialCheckStarted) { initialCheckStarted = true; return refresh(); } }
  // Tiny, temporary input gate only; OFF/ineligible config removes it. No
  // recorder, identity or network request is created by these listeners.
  wakeListeners(true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', deferredStart, { once: true });
  else deferredStart();
})(typeof window === 'undefined' ? null : window);
